import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { ConfigurationPage } from './ConfigurationPage';
import { validateDocument, validateRseDocument } from '../../services/validateService';
import { useLocation, useNavigate } from 'react-router-dom';
import iconValidar from '../../assets/icon-validar.svg';
import iconLoader from '../../assets/loader.gif';

const LEGACY_ACCEPTED_TYPES = [
    'application/pdf',
    'text/xml',
    'application/pkcs7-signature',
    'application/pkcs7-mime',
    'application/json'
];

export const ValidatorPage = () => {
    const [validatorMode, setValidatorMode] = useState(null);
    const isValidatorModeLoaded = validatorMode !== null;
    const isRseValidator = validatorMode === 'RSE';

    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();

    const refFrom = useRef();
    const fileInputRef = useRef();
    const fileInputRefOrigin = useRef();
    const [invalidFileType, setInvalidFileType] = useState(false);
    const [file, setFile] = useState(null);
    const [fileBlob, setFileBlob] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const [isSeparateVal, setIsSeparateVal] = useState(false);
    const [fileOrigin, setFileOrigin] = useState(null);
    const [fileBlobOrigin, setFileBlobOrigin] = useState(null);
    const [isDragOverOrigin, setIsDragOverOrigin] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [configurationVal, setConfigurationVal] = useState({
        defaultValues: {
            "extended_report": "true",
            "report_type": "html",
            "verify_incremental_updates": "true",
            "show_payload_json": "true",
            "emit_receipt": "false"
        }
    });
    const [report, setReport] = useState(null);
    const [rBase64, setRBase64] = useState(null);
    const [rFileName, setRFileName] = useState(null);

    const resolveValidatorMode = (configs) => {
        const mode = configs?.VALIDATOR_MODE;
        return typeof mode === 'string' && mode.trim() ? mode.toUpperCase() : 'LEGACY';
    };

    const getFileBlob = (uploadFile, onLoad) => {
        const reader = new FileReader();
        reader.onload = () => {
            const binary = atob(reader.result.split(',')[1]);
            const array = [];
            for (let i = 0; i < binary.length; i++) {
                array.push(binary.charCodeAt(i));
            }
            onLoad(new Blob([new Uint8Array(array)], { type: uploadFile.type }));
        };
        reader.readAsDataURL(uploadFile);
    };

    const isLegacyFileType = (uploadFile) => LEGACY_ACCEPTED_TYPES.includes(uploadFile.type);
    const isRseFileType = (uploadFile) => uploadFile.type === 'application/pdf';
    const getFileAccept = () => isRseValidator ? '.pdf' : '.p7s, .xml, .pdf, .p7m, .json';
    const getFileDetailKey = () => isRseValidator ? 'validator.detail-file-rse' : 'validator.detail-file';
    const getInvalidFileTypeKey = () => isRseValidator ? 'validator.invalid-file-type-rse' : 'validator.invalid-file-type';

    const canValidate = isRseValidator
        ? fileBlob !== null
        : (fileBlob !== null && isSeparateVal === false) || (fileBlob !== null && fileBlobOrigin !== null);

    const onSelectFile = () => {
        fileInputRef.current.click();
    };

    const onDeleteFile = () => {
        if (file) {
            fileInputRef.current.value = '';
            setFile(null);
            setFileBlob(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files) {
            uploadDocToValidated(e.dataTransfer.files[0]);
        }
    };

    const onFileChange = (e) => {
        if (e.target.files) {
            uploadDocToValidated(e.target.files[0]);
        }
    };

    const uploadDocToValidated = (uploadFile) => {
        const isValidFileType = isRseValidator ? isRseFileType(uploadFile) : isLegacyFileType(uploadFile);

        if (isValidFileType) {
            setFile(uploadFile);
            getFileBlob(uploadFile, (blob) => {
                setFileBlob(blob);
                setInvalidFileType(false);
            });
        } else {
            setInvalidFileType(true);
        }
    };

    const onSeparateSignValidation = () => {
        setIsSeparateVal(!isSeparateVal);
        onDeleteFileOrigin();
    };

    const onSelectFileOrigin = () => {
        fileInputRefOrigin.current.click();
    };

    const onDeleteFileOrigin = () => {
        if (fileOrigin) {
            fileInputRefOrigin.current.value = '';
            setFileOrigin(null);
            setFileBlobOrigin(null);
        }
    };

    const handleDragOverOrigin = (e) => {
        e.preventDefault();
        setIsDragOverOrigin(true);
    };

    const handleDragLeaveOrigin = (e) => {
        e.preventDefault();
        setIsDragOverOrigin(false);
    };

    const handleDropOrigin = (e) => {
        e.preventDefault();
        setIsDragOverOrigin(false);
        if (e.dataTransfer.files) {
            uploadDocToValidatedOrigin(e.dataTransfer.files[0]);
        }
    };

    const onFileChangeOrigin = (e) => {
        if (e.target.files) {
            uploadDocToValidatedOrigin(e.target.files[0]);
        }
    };

    const uploadDocToValidatedOrigin = (uploadFile) => {
        if (isLegacyFileType(uploadFile)) {
            setFileOrigin(uploadFile);
            getFileBlob(uploadFile, (blob) => {
                setFileBlobOrigin(blob);
                setInvalidFileType(false);
            });
        } else {
            setInvalidFileType(true);
        }
    };

    const onValidateDoc = async (data = configurationVal.defaultValues) => {
        if (isRseValidator) {
            if (!fileBlob) {
                return;
            }

            try {
                setIsValidating(true);
                const result = await validateRseDocument(fileBlob, file);
                navigate('/validated/rse', {
                    state: {
                        file,
                        fileBlob,
                        result
                    }
                });
            } catch (error) {
                navigate('/error', {
                    state: {
                        code: t('error.error-503'),
                        title: t('error.title-503'),
                        detailFirst: t('error.detail-first-503'),
                        detailSecond: t('error.detail-second-503')
                    }
                });
            } finally {
                setIsValidating(false);
            }
            return;
        }

        try {
            if (fileBlob) {
                setIsValidating(true);
                setConfigurationVal(data);
                const { reports, receiptBase64, receiptFilename } = await validateDocument(data, fileBlob, file, fileBlobOrigin, fileOrigin);
                setRBase64(receiptBase64);
                setRFileName(receiptFilename);
                setReport(reports);
            }
        } catch (error) {
            navigate('/error', {
                state: {
                    code: t('error.error-503'),
                    title: t('error.title-503'),
                    detailFirst: t('error.detail-first-503'),
                    detailSecond: t('error.detail-second-503')
                }
            });
        }
    };

    const onCancelValid = () => {
        setIsValidating(false);
    };

    useEffect(() => {
        if (report && isValidating) {
            navigate('/validated', {
                state: {
                    file: file,
                    report: report[0],
                    receiptBase64: rBase64,
                    receiptFilename: rFileName,
                    data: configurationVal,
                    fileBlob: fileBlob
                }
            });
        }
    }, [report]);

    useEffect(() => {
        try {
            const encodedConfigs = sessionStorage.getItem('configs');
            const configs = encodedConfigs ? JSON.parse(atob(encodedConfigs)) : null;
            setValidatorMode(resolveValidatorMode(configs));
        } catch (error) {
            setValidatorMode('LEGACY');
        }
    }, []);

    useEffect(() => {
        if (!isValidatorModeLoaded) {
            return;
        }

        if (state) {
            setFile(state.file);
            setFileBlob(state.fileBlob);
            if (state.data) {
                setConfigurationVal({ defaultValues: state.data });
            }
        }
        window.collapseContent();
        if (!isRseValidator) {
            window.checkboxCarga();
        }

    }, [state, isRseValidator, isValidatorModeLoaded]);

    if (!isValidatorModeLoaded) {
        return (
            <main className="main-content main-full">
                <div className="component-main-content-firma">
                    <div className="page-container" />
                </div>
            </main>
        );
    }

    return (
        <main className="main-content main-full">
            <div className="component-main-content-firma">
                <div className="page-container">
                    {invalidFileType && (
                        <div className="component-alert">
                            <div className="alert alert-error">
                                <span className="material-icons"> cancel </span>
                                <p> {t(getInvalidFileTypeKey())} </p>
                            </div>
                        </div>
                    )}

                    <div className="card-container">
                        <div className="card-head">
                            <h1 className="card-title">
                                <img src={iconValidar} className="img-fluid" alt={t('validator.title')} />
                                {t('validator.title')}
                            </h1>
                        </div>

                        <div className="card-grid">
                            <div className="section-carga">
                                <div className="component-carga-documentos">
                                    <div
                                        className={`carga-documento-content ${file ? 'carga-documento-content-disabled' : ''} ${isDragOver ? 'drag-over' : ''}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <span className="material-symbols-outlined"> cloud_upload </span>
                                        <p className="text"> {t('validator.drag-file')} </p>
                                        <p className="text-small"> {t('validator.or-prefer')} </p>

                                        <input
                                            ref={fileInputRef}
                                            className="file-upload-input"
                                            type="file"
                                            onChange={onFileChange}
                                            disabled={!!file}
                                            accept={getFileAccept()}
                                        />
                                        <button
                                            className="local-upload btn-medium btn-primary-outline"
                                            data-toggle="tooltip"
                                            data-placement="top"
                                            title=""
                                            onClick={onSelectFile}
                                            data-original-title="Desktop"
                                        >
                                            {t('validator.select-file')}
                                        </button>

                                        <p className="text-alert"> {t(getFileDetailKey())} </p>

                                        {file && (
                                            <div className="doc-detalle">
                                                <span className="material-symbols-outlined"> description </span>
                                                <p className="txt"> {file.name} <span className="size"> {Math.round(file.size / 1024)} Kb </span> </p>
                                                <button disabled={isValidating} className="delete-file" type="button" onClick={onDeleteFile}>
                                                    <span className="material-symbols-outlined"> delete </span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {!isRseValidator && (
                                    <>
                                        <div className="inline-group">
                                            <div className="form-group">
                                                <input
                                                    className="form-checkbox"
                                                    id="checkbox-carga"
                                                    type="checkbox"
                                                    name="optionesPresentacion"
                                                    checked={isSeparateVal}
                                                    onChange={onSeparateSignValidation}
                                                />
                                                <label className="form-label" htmlFor="checkbox-carga"> {t('validator.separate-sign-validation')} </label>
                                            </div>
                                        </div>

                                        <div className="carga-secundaria">
                                            <h4> {t('validator.upload-original-doc')} </h4>

                                            <div className="component-carga-documentos">
                                                <div
                                                    className={`carga-documento-content ${fileOrigin ? 'carga-documento-content-disabled' : ''} ${isDragOverOrigin ? 'drag-over' : ''}`}
                                                    onDragOver={handleDragOverOrigin}
                                                    onDragLeave={handleDragLeaveOrigin}
                                                    onDrop={handleDropOrigin}
                                                >
                                                    <span className="material-symbols-outlined"> cloud_upload </span>
                                                    <p className="text"> {t('validator.drag-file')} </p>
                                                    <p className="text-small"> {t('validator.or-prefer')} </p>

                                                    <input
                                                        ref={fileInputRefOrigin}
                                                        className="file-upload-input"
                                                        type="file"
                                                        onChange={onFileChangeOrigin}
                                                        disabled={!!fileOrigin}
                                                        accept=".p7s, .xml, .pdf, .p7m, .json"
                                                    />
                                                    <button
                                                        className="local-upload btn-medium btn-primary-outline"
                                                        data-toggle="tooltip"
                                                        data-placement="top"
                                                        title=""
                                                        onClick={onSelectFileOrigin}
                                                        data-original-title="Desktop"
                                                    >
                                                        {t('validator.select-file')}
                                                    </button>

                                                    <p className="text-alert"> {t('validator.detail-file')} </p>

                                                    {fileOrigin && (
                                                        <div className="doc-detalle">
                                                            <span className="material-symbols-outlined"> description </span>
                                                            <p className="txt"> {fileOrigin.name} <span className="size"> {Math.round(fileOrigin.size / 1024)} Kb </span> </p>
                                                            <button disabled={isValidating} className="delete-file" type="button" onClick={onDeleteFileOrigin}>
                                                                <span className="material-symbols-outlined"> delete </span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {!isRseValidator && (
                                <ConfigurationPage refFrom={refFrom} onSubmitForm={onValidateDoc} defaultValues={configurationVal} />
                            )}
                        </div>

                        <div className="card-actions">
                            {isValidating && !isRseValidator && (
                                <button onClick={onCancelValid} className="btn-medium btn-secondary"> {t('validator.cancel-button')} </button>
                            )}
                            {isValidating && (
                                <button className="btn-medium btn-primary"> {t('validator.validating-button')} <img src={iconLoader} style={{ width: '26px' }} className="img-fluid" /> </button>
                            )}
                            {!isValidating && (
                                <button
                                    className={`btn-medium btn-primary ${canValidate ? '' : 'disable-link'}`}
                                    onClick={() => isRseValidator ? onValidateDoc() : refFrom.current.submitForm()}
                                >
                                    {t('validator.validate-button')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};
